/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { B2B } from '@/components/blocks/B2B';
import { TYPES } from '@/data/constants/product';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { productIsA } from '@/utils/productIsA';
import { Alert, Box, Button, IconButton, Snackbar } from '@mui/material';
import { FC, Fragment, MouseEvent, useCallback, useContext, useMemo, useState } from 'react';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { ModalLoginFavorites } from '@/components/content/Cart/parts/ModalLoignFavorites';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { snackBarSX } from '@/components/content/Notifications/styles/snackbar';
import { alertSX } from '@/components/content/Notifications/styles/alert';
type Props = {
	standalone?: boolean;
	isIconFavorite?: boolean;
	inThumbnail?: boolean;
	disabled?: boolean;
	isInWishList?: boolean;
	checkIsInWishList?: () => void;
};
enum DRAWER_AUTH {
	AUTH = 'auth',
	LOGIN = 'login',
	REGISTER = 'register',
}

export const ProductDetailsAddToWishList: FC<Props> = ({
	standalone = false,
	isIconFavorite = false,
	inThumbnail = false,
	disabled: _disabled,
	isInWishList,
	checkIsInWishList,
}) => {
	const { selection, loginStatus, wishLists, addToWishList, product } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails> & ReturnType<typeof useSkuListTable>;
	const isBundle = productIsA(product, TYPES.bundle);
	const sku = !isBundle ? selection?.sku : product;
	const buyable = !isBundle ? selection?.buyable : true;
	const quantity = !isBundle ? selection?.quantity : 1;
	const { prices } = getProductDisplayInfo(sku, product);
	const disabled = !buyable || !prices?.offer;
	const dtId = useMemo(() => `wishlist-selection${standalone ? '-standalone' : ''}`, [standalone]);
	const [drawerAuth, setDrawerAuth] = useState<DRAWER_AUTH | null>(null);
	const isMobile = useIsMobile();
	const handleOnDrawerAuthClear = () => {
		setDrawerAuth(null);
	};
	const handleOnDrawerGoTo = (drawer: DRAWER_AUTH) => () => {
		setDrawerAuth(drawer);
	};

	const [message, setMessage] = useState('');
	const onClickFavorites = useCallback(
		(partNumber = '', quantity: number) =>
			async (_event: MouseEvent<HTMLElement>) => {
				if (!loginStatus) {
					setDrawerAuth(DRAWER_AUTH.LOGIN);
				} else {
					const wishListFavorites = wishLists.find((elm) => elm.description === 'Favoritos');
					if (wishListFavorites) {
						await addToWishList(partNumber, quantity, wishListFavorites)();
						setMessage('El artículo se ha añadido correctamente a la lista');
						checkIsInWishList?.();
					}
				}
			},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[addToWishList, loginStatus, wishLists]
	);
	const onClear = () => {
		setMessage('');
	};
	return (
		<Fragment>
			<Snackbar
				open={message ? true : false}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				autoHideDuration={5000}
				onClose={onClear}
				ClickAwayListenerProps={{
					onClickAway: onClear,
					mouseEvent: 'onMouseDown',
				}}
				sx={snackBarSX}
			>
				<Box>
					<Box>
						<Alert severity={'success'} onClose={onClear} sx={alertSX} icon={false}>
							{message}
						</Alert>
					</Box>
				</Box>
			</Snackbar>
			<B2B is={false}>
				<>
					{isIconFavorite ? (
						<IconButton
							onClick={onClickFavorites(sku?.partNumber, quantity)}
							id="pdp-favorite-button"
							aria-label="fingerprint"
							sx={{
								backgroundColor: '#F1F1F1',
								position: 'absolute',
								bottom: inThumbnail ? '115px' : '65px',
								left: inThumbnail ? undefined : '19px',
								right: inThumbnail ? '19px' : undefined,
								zIndex: 1001,
							}}
							disabled={_disabled}
						>
							{isInWishList ? <Favorite sx={{ color: '#2686e2' }} /> : <FavoriteBorder />}
						</IconButton>
					) : (
						<Button
							data-testid={dtId}
							id={dtId}
							onClick={onClickFavorites(sku?.partNumber, quantity)}
							disabled={disabled}
							sx={{
								background: '#F1F1F1',
								borderRadius: '22px',
								color: '#2C2C2C',
								fontSize: 'var(--font-size-body)',
								fontWeight: 'bold',
								height: '50px',
								minWidth: '153px',
							}}
							startIcon={<FavoriteBorderIcon />}
						>
							{'Favoritos'}
						</Button>
					)}
					{!isMobile && DRAWER_AUTH.LOGIN === drawerAuth ? (
						<ModalLoginFavorites
							onClose={handleOnDrawerAuthClear}
							onGoRegister={handleOnDrawerGoTo(DRAWER_AUTH.REGISTER)}
						/>
					) : null}
				</>
			</B2B>
		</Fragment>
	);
};
